import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Header from '@/components/Header'
import { useAuth } from '@/contexts/AuthContext'
import { getAllMerchants, createMerchant, updateMerchant, deleteMerchant } from '@/services/api'

interface MerchantItem {
  id: string
  name: string
  status?: string
  updated_at?: string
}

export default function Merchants() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [filters, setFilters] = useState<{ name: string; status: string; page: number; limit: number }>({ name: '', status: '', page: 1, limit: 10 })
  const [list, setList] = useState<MerchantItem[]>([])
  const [total, setTotal] = useState(0)
  const [creating, setCreating] = useState<{ name: string; status: 'active' | 'disabled' }>({ name: '', status: 'active' })
  const [editId, setEditId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<{ name: string; status: 'active' | 'disabled' }>({ name: '', status: 'active' })
  const pageCount = Math.ceil(total / filters.limit) || 1

  useEffect(() => { load() }, [filters])

  const load = async () => {
    try {
      const r = await getAllMerchants({ name: filters.name || undefined, status: filters.status || undefined, page: filters.page, limit: filters.limit })
      setList(r.data?.merchants ?? [])
      setTotal(r.data?.pagination?.total ?? 0)
    } catch {}
  }

  const saveNew = async () => {
    if (!creating.name) return
    try {
      await createMerchant({ name: creating.name, status: creating.status })
      setCreating({ name: '', status: 'active' })
      await load()
      alert('已创建商户')
    } catch {
      alert('创建失败')
    }
  }

  const saveEdit = async (id: string) => {
    try {
      await updateMerchant(id, { name: editForm.name, status: editForm.status })
      setEditId(null)
      await load()
      alert('已保存')
    } catch {
      alert('保存失败')
    }
  }

  const remove = async (id: string) => {
    if (!confirm('确定要删除该商户吗？')) return
    try {
      await deleteMerchant(id)
      await load()
      alert('已删除')
    } catch {
      alert('删除失败')
    }
  }

  if (!user || user.role !== 'system_admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white shadow rounded p-6">
          <div className="mb-4">权限不足：仅系统管理员可使用商户管理</div>
          <button className="px-3 py-2 border rounded" onClick={() => navigate('/dashboard')}>返回控制台</button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="商户管理" backTo="/dashboard" />
      <main className="max-w-6xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <input className="border rounded px-3 py-2" placeholder="商户名称" value={filters.name} onChange={e=>setFilters({ ...filters, name: e.target.value, page: 1 })} />
            <select className="border rounded px-3 py-2" value={filters.status} onChange={e=>setFilters({ ...filters, status: e.target.value, page: 1 })}>
              <option value="">全部状态</option>
              <option value="active">启用</option>
              <option value="disabled">停用</option>
            </select>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="mb-3 font-semibold">创建商户</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <input className="border rounded px-3 py-2" placeholder="商户名称" value={creating.name} onChange={e=>setCreating({ ...creating, name: e.target.value })} />
            <select className="border rounded px-3 py-2" value={creating.status} onChange={e=>setCreating({ ...creating, status: e.target.value as any })}>
              <option value="active">启用</option>
              <option value="disabled">停用</option>
            </select>
          </div>
          <div className="mt-3"><button className="bg-wechat-green text-white px-4 py-2 rounded" onClick={saveNew}>保存</button></div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left">名称</th>
                  <th className="px-3 py-2 text-left">状态</th>
                  <th className="px-3 py-2 text-left">更新时间</th>
                  <th className="px-3 py-2 text-left">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {list.map(m => (
                  <tr key={m.id}>
                    {editId===m.id ? (
                      <>
                        <td className="px-3 py-2"><input className="border rounded px-2 py-1 w-full" value={editForm.name} onChange={e=>setEditForm({ ...editForm, name: e.target.value })} /></td>
                        <td className="px-3 py-2">
                          <select className="border rounded px-2 py-1 w-full" value={editForm.status} onChange={e=>setEditForm({ ...editForm, status: e.target.value as any })}>
                            <option value="active">启用</option>
                            <option value="disabled">停用</option>
                          </select>
                        </td>
                        <td className="px-3 py-2">{m.updated_at || '-'}</td>
                        <td className="px-3 py-2 flex gap-2"><button className="bg-wechat-green text-white px-3 py-1 rounded" onClick={()=>saveEdit(m.id)}>保存</button><button className="px-3 py-1 border rounded" onClick={()=>setEditId(null)}>取消</button></td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-2">{m.name}</td>
                        <td className="px-3 py-2">{m.status || 'active'}</td>
                        <td className="px-3 py-2">{m.updated_at || '-'}</td>
                        <td className="px-3 py-2 flex gap-3"><button className="text-wechat-green" onClick={()=>{ setEditId(m.id); setEditForm({ name: m.name, status: (m.status as any) || 'active' }) }}>编辑</button><button className="text-red-600" onClick={()=>remove(m.id)}>删除</button></td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center gap-2"><button disabled={filters.page<=1} onClick={()=>setFilters({ ...filters, page: filters.page-1 })} className="px-3 py-1 border rounded">上一页</button><span>第 {filters.page} / {pageCount} 页</span><button disabled={filters.page>=pageCount} onClick={()=>setFilters({ ...filters, page: filters.page+1 })} className="px-3 py-1 border rounded">下一页</button></div>
        </div>
      </main>
    </div>
  )
}

